import { Observable } from 'rxjs/Observable';
import 'rxjs/add/observable/fromPromise';
import 'rxjs/add/observable/empty';
import 'rxjs/add/operator/catch';
import { getCaptchaV3Key } from 'settings';
import { enPaymentMethod } from 'enums/enPaymentMethod';
import { paymentService } from './PaymentService';
import goofyService from './GoofyService';

export const EXPECTED_ACTION = 'checkout_payment_sent';
const SCORE_ELIGIBLE_METHODS = [enPaymentMethod.CREDIT_CARD, enPaymentMethod.ONE_CLICK_BUY];
const SCRIPT_TIMEOUT_MS = 10000;

let scriptLoaded = false;

export function prepareRecaptchaForV2() {
  document.querySelectorAll('script[src*="recaptcha/enterprise.js"]').forEach((script) => {
    script.parentNode?.removeChild(script);
  });

  if (window.grecaptcha && typeof window.grecaptcha.render !== 'function') {
    delete (window as any).grecaptcha;
  }

  scriptLoaded = false;
}

export class CaptchaScoreService {
  isEnabled() {
    return !!getCaptchaV3Key();
  }

  isEligibleMethod(method?: enPaymentMethod) {
    return !!method && SCORE_ELIGIBLE_METHODS.includes(method);
  }

  shouldPreload(ignoreCaptchaScoreParam?: boolean | string | number) {
    if (window.IS_DEVELOPMENT === true && !!ignoreCaptchaScoreParam) {
      return false;
    }

    return this.isEnabled();
  }

  shouldRunForMethod(method?: enPaymentMethod, ignoreCaptchaScoreParam?: boolean | string | number) {
    if (window.IS_DEVELOPMENT === true && !!ignoreCaptchaScoreParam) {
      return false;
    }

    return this.isEnabled() && this.isEligibleMethod(method);
  }

  preloadScript(): Observable<void> {
    const captchaV3Key = getCaptchaV3Key();

    if (!captchaV3Key) {
      return Observable.empty<void>();
    }

    return Observable.fromPromise(this.loadScript(captchaV3Key)).catch(() => Observable.empty<void>());
  }

  execute(): Observable<string> {
    // Mesmo tracker do v2 (captcha exibido)
    goofyService.track({
      flow: 'sale',
      trackerId: window.GOOFY_ID,
      step: 'captcha_shown',
      data: { hasCaptcha: true, captchaVersion: 'v3' }
    });

    const captchaV3Key = getCaptchaV3Key();

    const tokenPromise = this.loadScript(captchaV3Key)
      .then(() => this.requestToken(captchaV3Key))
      .then((token) => {
        paymentService.setData({ captchaScoreToken: token });

        // Mesmo tracker do v2 (captcha resolvido)
        goofyService.track({
          flow: 'sale',
          trackerId: window.GOOFY_ID,
          step: 'captcha_resolved',
          data: { captchaVersion: 'v3' }
        });

        return token;
      });

    return Observable.fromPromise(tokenPromise);
  }

  private loadScript(captchaV3Key: string): Promise<void> {
    if (!captchaV3Key) {
      return Promise.reject(new Error('recaptcha v3 site key is not configured'));
    }

    if (scriptLoaded || (window.grecaptcha && window.grecaptcha.enterprise)) {
      return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      const timeoutId = window.setTimeout(() => {
        script.onload = null;
        script.onerror = null;
        scriptLoaded = false;
        reject(new Error('recaptcha enterprise script load timeout'));
      }, SCRIPT_TIMEOUT_MS);

      script.onload = () => {
        clearTimeout(timeoutId);
        scriptLoaded = true;
        resolve();
      };

      script.onerror = () => {
        clearTimeout(timeoutId);
        scriptLoaded = false;
        reject(new Error('recaptcha enterprise script failed to load'));
      };

      script.setAttribute('src', `//www.google.com/recaptcha/enterprise.js?render=${captchaV3Key}`);
      document.body.appendChild(script);
    });
  }

  private requestToken(captchaV3Key: string): Promise<string> {
    return new Promise((resolve, reject) => {
      if (!window.grecaptcha || !window.grecaptcha.enterprise || !window.grecaptcha.enterprise.ready) {
        reject(new Error('recaptcha enterprise not loaded'));
        return;
      }

      window.grecaptcha.enterprise.ready(() => {
        window.grecaptcha.enterprise.execute(captchaV3Key, { action: EXPECTED_ACTION }).then(resolve).catch(reject);
      });
    });
  }
}

// tslint:disable-next-line:variable-name
export const CaptchaScoreServiceFactory = () => new CaptchaScoreService();

export const captchaScoreService = CaptchaScoreServiceFactory();
